import { z } from "zod"

const OptionalBooleanFlagSchema = z
  .union([
    z.boolean(),
    z.enum(["true", "false", "1", "0"]).transform((value) => value === "true" || value === "1"),
    z
      .number()
      .int()
      .min(0)
      .max(1)
      .transform((value) => value === 1),
  ])
  .optional()
  .catch(undefined)

export const UserSchema = z.object({
  extension: z.any(),
  id: z.number().int().gte(0),
  lastName: z.string(),
  email: z.string().email().optional().catch(undefined),
  disableRecordingWarning: z.boolean(),
  disableCheckOut: z.boolean(),
  name: z.string(),
  isOwner: OptionalBooleanFlagSchema,
  isAdmin: OptionalBooleanFlagSchema,
  canEavesdrop: z.boolean(),
})

export const NextActionSchema = z.object({
  id: z.number().int().gte(0),
  questionnaireId: z.number().int().gte(0).nullish(),
  label: z.string(),
})

export const DepartmentSchema = z.object({
  id: z.number().int().gte(0),
  users: z.number().int().gte(0).array(),
  name: z.string(),
  hasQueueing: z.boolean(),
  deleted: z.boolean(),
})
export type TDepartment = z.infer<typeof DepartmentSchema>

export const DispositionSchema = z.object({
  id: z.number().int().gte(0),
  label: z.string(),
})
export type TDisposition = z.infer<typeof DispositionSchema>

export const RingoNumberSchema = z.object({
  id: z.number().int().gte(0),
  name: z.string(),
  number: z.string(),
  main: z.boolean().nullish(),
})

export const BaseCallerIDSchema = z.object({
  number: z.string(),
  label: z.string(),
  type: z.string(),
  selected: z.boolean(),
})
export type TBaseCallerID = z.infer<typeof BaseCallerIDSchema>

export const CallerIDSchema = z
  .object({
    number: z.string().nullish(),
    label: z.string().nullish(),
    type: z.string().nullish(),
    selected: z.boolean().nullish().optional(),
  })
  .nullish()
export type TCallerID = z.infer<typeof CallerIDSchema>

export const VoicemailDropSchema = z.object({
  id: z.number().int().gte(0),
  alias: z.string(),
  url: z.string().url(),
})

export const EndpointSchema = z.object({
  id: z.number().int().gte(0).nullish(),
  callBlast: z.boolean().nullish(),
  personId: z.number().int().gte(0).nullish(),
  label: z.string().nullish(),
  number: z.string().nullish(),
  type: z.string().nullish(),
  deviceId: z.any().nullish(),
})

export const PhoneStatusSchema = z.enum(["TALKING", "AVAILABLE", "RINGING", "NO_ANSWER_COOLDOWN"])
export type TPhoneStatus = z.infer<typeof PhoneStatusSchema>

export const RingSchema = z.object({
  id: z.number().int(),
  agentId: z.number().int().nullish(),
  direction: z.enum(["in", "ou"]).nullish(),
  hold: z.boolean().nullish(),
  isInstaCall: z.boolean().nullish(),
  muted: z.boolean().nullish(),
  needsMandatoryDisposition: z.boolean().nullish(),
  other: z
    .object({
      id: z.number().int(),
      name: z.string().nullish(),
      number: z.string().nullish(),
      tz: z.string().nullish(),
    })
    .nullish(),
  recording: z.boolean().nullish(),
  recordingUrl: z.string().url().nullish(),
  startTime: z.coerce.date().nullish(),
  transferred: z.boolean().nullish(),
  transferring: z.boolean().nullish(),
  type: z.string().nullish(),
  localPresenceNumber: z.string().nullish(),
})
export type TRing = z.infer<typeof RingSchema>

export const UserStatusSchema = z.object({
  accountId: z.coerce.number().int(),
  accountState: z.string(),
  accountSeats: z.number().int().gte(0),
  accountSignupChannel: z.string().nullish(),
  accountSignupPlan: z.string().nullish(),
  emailMarketingOptIn: z.boolean().nullish(),
  smsOptIn: z.boolean().nullish(),
  vmdropSetup: z.boolean(),
  disableCheckOut: z.boolean(),
  checkedIn: z.boolean(),
  phoneStatus: PhoneStatusSchema,
  id: z.coerce.number().int(),
  name: z.coerce.string(),
  lastName: z.coerce.string(),
  powerDialer: z.boolean(),
  companyName: z.coerce.string(),
  email: z.coerce.string(),
  disableRecordingWarnings: z.boolean().nullish(),
  departmentIds: z.number().int().gte(0).array(),
  isOwner: z.coerce.boolean(),
  isAdmin: z.coerce.boolean(),
  canAccessMonitor: z.coerce.boolean(),
  fullName: z.coerce.string(),
  canEavesdrop: z.boolean(),
  accountSmsEnabled: z.coerce.boolean(),
  ring: RingSchema.nullish(),
  instaCall: RingSchema.nullish(),
  // The data below is not type-safe
  // TODO: In the future this should be typed
  dateCreated: z.any(),
  crmSeats: z.any(),
  callMinutesLast30Days: z.any(),
  accountCallMinutesLast30Days: z.any(),
  dialingSessionsLast30Days: z.any(),
  accountDialingSessionsLast30Days: z.any(),
  accountTrialType: z.string().nullish(),
  accountTrialActivated: z.any(),
  rg_acct_hash: z.any(),
  powerDialing: z.boolean().nullish(),
  snoozingSince: z.number().nullish(),
})
export type TUserStatus = z.infer<typeof UserStatusSchema>

export const AccountSchema = z.object({
  callRecordingTranscription: z.boolean().nullish(),
  liveTranscription: z.boolean().nullish(),
  rg_acct_hash: z.string().nullish(),
  trialActivated: z.boolean().nullish(),
  createdAsActivated: z.boolean().nullish(),
  users: UserSchema.array(),
  accountSid: z.string(),
  sendSmsEnabled: z.boolean().nullish(),
  nextActions: NextActionSchema.array(),
  inTrial: z.boolean().nullish(),
  enableClickToCallOnlyInsideCrm: z.boolean(),
  departments: DepartmentSchema.array(),
  syncServiceSid: z.string(),
  timeZone: z.string(),
  expiredActiveDaysLeft: z.number().int(),
  dispositions: DispositionSchema.array(),
  powerDialerEnabled: z.boolean(),
  trialExpired: z.boolean().nullish(),
  trialConsumed: z.boolean().nullish(),
  trialDaysRemaining: z.number().nullish(),
  trialMinutesRemaining: z.number().nullish(),
  isSoloFree: z.boolean().nullish(),
  soloTrialConsumed: z.boolean().nullish(),
  soloTrialMinutesRemaining: z.number().nullish(),
  soloTrialCurrentPeriodResetDate: z.string().nullish(),
  showSequenceFeature: z.boolean().nullish(),
  // Email templates?
  machineDetectionEnabled: z.boolean().nullish(),
  crm: z.object({
    id: z.string(),
    availableText: z.string(),
    alias: z.string(),
    name: z.string(),
    saveNoteText: z.string(),
  }),
  enableEnterPhoneNumber: z.boolean().nullish(),
  testNumber: z.string().nullish(),
  timeToRingEndpointsSeconds: z.number().int().gte(0),
  status: z.string(),
  disableReferralLink: z.boolean().nullish(),
  blackListed: z.boolean(),
  adminEmail: z.string().email(),
  videoCallEnabled: z.boolean().nullish(),
  templatesEnabled: z.boolean().nullish(),
  // SMS templates?
  customCallerIdEnabled: z.boolean().nullish(),
  trustHubStatuses: z
    .object({
      customerProfileStatus: z.string().nullish(),
      smsVerificationStatus: z.string().nullish(),
      a2pProfileStatus: z.string().nullish(),
      shakenStirStatus: z.string().nullish(),
      cnamStatus: z.string().nullish(),
      voiceIntegrityStatus: z.string().nullish(),
      brandRegistrationStatus: z.string().nullish(),
      campaignStatus: z.string().nullish(),
      lastUpdated: z.string().nullish(),
    })
    .nullish(),
  clusterId: z.string(),
  ringoNumbers: RingoNumberSchema.array(),
  supportPhoneNumber: z.string(),
  teamInvited: z.boolean().nullish(),
})
export type TAccount = z.infer<typeof AccountSchema>

export const LastRingSchema = z.object({
  id: z.number().int(),
})
export type TLastRing = z.infer<typeof LastRingSchema>

export const ClusterSchema = z.object({
  adminUrlS: z.coerce.string().url(),
  apiUrlS: z.coerce.string().url(),
  chromeExtensionWebstoreId: z.coerce.string(),
  launcherUrlS: z.coerce.string().url(),
  remoteUrlS: z.coerce.string().url(),
  voiceUrlS: z.coerce.string().url(),
})
export type TCluster = z.infer<typeof ClusterSchema>

export const UserPreferencesSchema = z.object({
  endPointStrategy: z.string(),
  callerIds: CallerIDSchema.array(),
  voicemailDrops: VoicemailDropSchema.array().nullish(),
  pvmEnabled: z.boolean(),
  nylasAccount: z
    .object({
      syncState: z.string(),
      lastSuccessfulConnection: z.coerce.date(),
      emailAddress: z.string().email(),
      provider: z.string(),
    })
    .nullish(),
  endPoints: EndpointSchema.array().nullish(),
  autoEmail: z.boolean(),
  autoEmailTemplateId: z.number().int().gte(0).nullish(),
  autoSms: z.boolean(),
  autoSmsTemplateId: z.number().int().gte(0).nullish(),
  autoVoicemailDrop: z.boolean(),
  autoVmdAudioFileId: z.number().int().gte(0).nullish(),
  webEndPointEnabled: z.boolean(),
  gettingStarted: z.boolean(),
  deviceEndPointEnabled: z.boolean(),
  autoProgress: z.boolean(),
  powerDialerTargetType: z.string().nullish(),
  powerDialerTargetId: z.number().int().gte(0).nullish(),
  personalListSequenceId: z.number().int().gte(0).nullish(),
  personalListId: z.number().int().gte(0).nullish(),
  todoListSequenceId: z.number().int().gte(0).nullish(),
  todoListId: z.number().int().gte(0).nullish(),
  snoozeTime: z.number().int().gte(0).nullish(),
})
export type TPreferences = z.infer<typeof UserPreferencesSchema>

export const DialerItemSchema = z
  .object({
    _id: z.string().nullish(),
    tz: z.string().nullish(),
    tz_offset_millis: z.coerce.number().int().nullish(),
    currentStep: z
      .object({
        actionType: z.string().nullish(),
        contactPersonId: z.coerce.number().int().nullish(),
        contactPersonName: z.string().nullish(),
        idx: z.coerce.number().int().nullish(),
        instaCallId: z.coerce.number().int().nullish(),
        objectId: z.any().nullish(),
        objectType: z.string().nullish(),
        result: z.any().nullish(),
        scheduledAt: z.string().nullish(),
        status: z.string().nullish(),
        templateId: z.any().nullish(),
        templateName: z.string().nullish(),
      })
      .nullish(),
  })
  .passthrough()

const DialerCallSchema = z.object({
  DNC: z.boolean().nullish(),
  age: z.any().nullish(),
  callCounter: z.number().nullish(),
  company: z.string().nullish(),
  customData: z.record(z.any()).nullish(),
  email: z.string().nullish(),
  id: z.coerce.number().int(),
  lastDisposition: z.any().nullish(),
  lastNote: z.record(z.any()).or(z.string()).nullish(),
  lastTimeCalled: z.any().nullish(),
  last_name: z.string().nullish(),
  name: z.string().nullish(),
  phone: z.string().nullish(),
  phoneNumbers: z
    .array(
      z.object({
        id: z.number().nullish(),
        callBlast: z.boolean().nullish(),
        device: z.any().nullish(),
        number: z.string().nullish(),
        label: z.string().nullish(),
      })
    )
    .nullish(),
  remoteType: z.string().nullish(),
  removedTimestamp: z.number().nullish(),
  title: z.string().nullish(),
  tz: z.string().nullish(),
  tz_offset_millis: z.number().nullish(),
  _id: z.string().nullish(),
})

const AllowedDialerCallSchema = z.object({ id: z.number().int() }).passthrough()
const DialerListStatsSchema = z.object({
  totalCount: z.coerce.number().int().catch(0).default(0),
  meetCriteria: z.coerce.number().int().catch(0).default(0),
  called: z.coerce.number().int().catch(0).default(0),
})

export const DialerListSchema = z
  .object({
    lastCall: AllowedDialerCallSchema.or(z.object({})).nullish(),
    list: DialerItemSchema.array().nullish(),
    pastCalls: z.array(DialerCallSchema).nullish(),
    stats: DialerListStatsSchema.nullish(),
  })
  .nullish()

export type TDialerList = z.infer<typeof DialerListSchema>

export const SmartViewSchema = z.object({
  contact: z.object({
    id: z.number().int().gte(0),
  }),
})
export type TSmartView = z.infer<typeof SmartViewSchema>

export const LoginUserDataSchema = z.object({
  apiToken: z.coerce.string(),
  authToken: z.coerce.string(),
  cluster: ClusterSchema,
  account: AccountSchema,
  user: UserStatusSchema,
  preferences: UserPreferencesSchema,
  baseUrl: z.coerce.string().url(),
  twilio: z.object({
    twimlToken: z.coerce.string(),
    syncToken: z.coerce.string(),
    secondsTtl: z.coerce.number().int(),
    ids: z.object({
      userStatus: z.coerce.string(),
      userPreferences: z.coerce.string(),
      smartView: z.coerce.string(),
      lastRing: z.coerce.string(),
      smsHistories: z.coerce.string(),
      contacts: z.coerce.string(),
      intention: z.coerce.string(),
      roster: z.coerce.string(),
      departmentStats: z.coerce.string(),
      userStats: z.coerce.string(),
      account: z.coerce.string(),
      dialerList: z.coerce.string(),
      cluster: z.coerce.string(),
      liveCalls: z.coerce.string(),
      paymentStatus: z.coerce.string(),
      recentCalls: z.coerce.string(),
    }),
  }),
  dynamoDb: z.object({
    authorizationToken: z.coerce.string(),
    tokenTtlInSeconds: z.coerce.number().int(),
    accountId: z.any(),
    url: z.coerce.string().url(),
  }),
  xAuth: z.string().optional(),
})
export type TLoginUserData = z.infer<typeof LoginUserDataSchema>
